import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    n: "01",
    title: "Invest In Yourself",
    body: "AB Hunter Consulting USA is a full-service strategic consulting practice working side by side with clients to provide tailored strategic plans for growth and improvement — capacity building, organizational health, and bridging the gap between educational institutions and the community.",
  },
  {
    n: "02",
    title: "The Threefold Mission",
    body: "Build bridges that improve educational outcomes and close achievement gaps. Elevate communities by enhancing organizational health and forging meaningful partnerships. Transform leaders by building their capacity to positively shift environments.",
  },
  {
    n: "03",
    title: "It's Simple — We Get Results",
    body: "A track record of yielding academic growth in struggling schools, mentoring thousands of educators and youth, and building leadership skill sets across sectors. Specialized knowledge, data-driven operations, and strategies that deliver.",
  },
  {
    n: "04",
    title: "A Culture of Empowerment",
    body: "Best practices and proven research — paired with values of inclusivity, empathy, and global citizenship. A personalized culture of mentorship cultivating a generation of extraordinary, versatile leaders equipped to shift atmospheres.",
  },
];

export default function Manifesto() {
  const [open, setOpen] = useState(0);

  return (
    <section id="about" data-testid="manifesto-section" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-16 flex items-end justify-between gap-6"
      >
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">The Manifesto</span>
          <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
            Why AB Hunter exists —<br className="hidden sm:block" /> in four chapters.
          </h2>
        </div>
        <span className="hidden font-mono text-xs tracking-[0.3em] text-muted-foreground sm:block">01 — 04</span>
      </motion.div>

      {/* Mobile: accordion */}
      <div className="space-y-3 md:hidden" data-testid="manifesto-accordion">
        {CHAPTERS.map((c, i) => {
          const isOpen = open === i;
          return (
            <div key={c.n} data-testid={`manifesto-accordion-item-${c.n}`}
              className={`overflow-hidden rounded-2xl border transition-colors duration-300 ${isOpen ? "border-gold/40 bg-ink-card" : "border-gold/15 bg-ink"}`}>
              <button
                data-testid={`manifesto-accordion-toggle-${c.n}`}
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 p-5 text-left"
              >
                <span className="flex items-center gap-3.5">
                  <span className={`font-mono text-xs tracking-[0.2em] transition-colors ${isOpen ? "text-gold" : "text-gold/50"}`}>{c.n}</span>
                  <span className="font-display text-base font-semibold leading-snug">{c.title}</span>
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-gold transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <p className="px-5 pb-6 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Desktop: unchanged grid */}
      <div className="hidden gap-px overflow-hidden rounded-2xl border border-gold/15 bg-gold/10 md:grid md:grid-cols-2">
        {CHAPTERS.map((c, i) => (
          <motion.article
            key={c.n}
            data-testid={`manifesto-chapter-${c.n}`}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
            className="group relative bg-ink p-8 transition-colors duration-500 hover:bg-ink-card sm:p-12"
          >
            <span className="font-mono text-xs tracking-[0.3em] text-gold/60 transition-colors duration-500 group-hover:text-gold">
              {c.n} / 04
            </span>
            <h3 className="mt-5 font-display text-xl font-semibold sm:text-2xl">{c.title}</h3>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">{c.body}</p>
            <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gold transition-all duration-700 group-hover:w-full" />
          </motion.article>
        ))}
      </div>
    </section>
  );
}
